import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { AuditLogEntry, Paged } from '../api/types'
import { auditLogsApi } from '../api/users'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { formatDateTime, formatInt } from '../lib/format'

const PAGE_SIZE = 50

export function AuditLogPage() {
  const [page, setPage] = useState(1)
  const logs = useAsync<Paged<AuditLogEntry>>(() => auditLogsApi.list(page, PAGE_SIZE), [page])

  const items = logs.data?.items ?? []
  const total = logs.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="wrap">
      <div className="phead">
        <div>
          <div className="crumb">
            <Link to="/app">Home</Link> / Audit log
          </div>
          <h2>Audit log</h2>
          <p className="fs13 muted" style={{ margin: '6px 0 0' }}>
            Who did what, when, and from where. Entries are immutable.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <h3>{formatInt(total)} entries</h3>
        </div>

        {logs.error ? (
          <ErrorState error={logs.error} onRetry={logs.reload} />
        ) : logs.loading && !logs.data ? (
          <LoadingState label="Loading the audit log…" />
        ) : items.length === 0 ? (
          <EmptyState icon="≡" title="Nothing logged yet" body="Actions appear here as you use the app." />
        ) : (
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 170 }}>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Detail</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="fs12 mono">{formatDateTime(entry.occurredAt)}</td>
                    <td className="fs13 mono">{entry.actorEmail ?? 'system'}</td>
                    <td className="fs13">{entry.action}</td>
                    <td className="fs12 mono faint">
                      {entry.entityType ? `${entry.entityType} ${entry.entityId ?? ''}` : '—'}
                    </td>
                    <td className="fs13">{entry.summary ?? '—'}</td>
                    <td className="fs12 mono faint">{entry.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="card-f">
          <span className="fs13 muted">
            Page {page} of {lastPage}
          </span>
          <span className="spacer" />
          <button
            className="btn btn-g btn-sm"
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            ← Previous
          </button>
          <button
            className="btn btn-g btn-sm"
            type="button"
            disabled={page >= lastPage}
            onClick={() => setPage((current) => current + 1)}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}
